<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import NotificacaoDialog from './NotificacaoDialog.vue';

const store = useStore();
const getters = useStoreGetters();

const showDialog = ref(false);
const editingNotification = ref(null);

const notifications = computed(
  () => getters['scheduledNotifications/getScheduledNotifications'].value
);
const uiFlags = computed(
  () => getters['scheduledNotifications/getUIFlags'].value
);

const inboxes = computed(() => {
  const allInboxes = getters['inboxes/getInboxes'].value || [];
  return allInboxes.filter(
    i =>
      i.channel_type === 'Channel::Whatsapp' ||
      i.channel_type === 'Channel::Api'
  );
});

onMounted(() => {
  store.dispatch('scheduledNotifications/get');
  store.dispatch('inboxes/get');
  store.dispatch('contactLists/get');
});

const handleNew = () => {
  editingNotification.value = null;
  showDialog.value = true;
};

const handleEdit = notification => {
  editingNotification.value = notification;
  showDialog.value = true;
};

const handleCloseDialog = () => {
  editingNotification.value = null;
  showDialog.value = false;
};

const handleDelete = async notification => {
  try {
    await store.dispatch('scheduledNotifications/delete', notification.id);
    useAlert('Notificação excluída com sucesso');
  } catch {
    useAlert('Erro ao excluir notificação');
  }
};

const handleCancel = async notification => {
  try {
    await store.dispatch('scheduledNotifications/cancel', notification.id);
    useAlert('Notificação cancelada com sucesso');
  } catch {
    useAlert('Erro ao cancelar notificação');
  }
};

const confirmingResend = ref(null);

const handleResend = notification => {
  confirmingResend.value = notification;
};

const confirmResend = async () => {
  try {
    await store.dispatch(
      'scheduledNotifications/resend',
      confirmingResend.value.id
    );
    useAlert('Notificação reagendada para reenvio');
  } catch {
    useAlert('Erro ao reenviar notificação');
  } finally {
    confirmingResend.value = null;
  }
};

const cancelResend = () => {
  confirmingResend.value = null;
};

const statusLabel = status => {
  const labels = {
    pending: 'Pendente',
    running: 'Enviando',
    completed: 'Concluída',
    failed: 'Falhou',
    cancelled: 'Cancelada',
  };
  return labels[status] || status;
};

const statusColor = status => {
  const colors = {
    pending: 'text-n-amber-11',
    running: 'text-n-blue-11',
    completed: 'text-n-teal-11',
    failed: 'text-n-ruby-11',
    cancelled: 'text-n-slate-11',
  };
  return colors[status] || '';
};

const formatDate = dateStr => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>

<template>
  <div class="flex flex-col flex-1 h-full p-6">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-n-slate-12">
        Notificações Agendadas
      </h1>
      <button
        class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
        @click="handleNew"
      >
        Nova Notificação
      </button>
    </div>

    <div v-if="uiFlags.isFetching" class="flex items-center justify-center py-8">
      <span class="text-n-slate-11">Carregando...</span>
    </div>

    <div v-else-if="notifications.length === 0" class="flex flex-col items-center justify-center py-16">
      <div class="i-lucide-bell-off w-12 h-12 text-n-slate-9 mb-4" />
      <p class="text-n-slate-11">
        Nenhuma notificação agendada
      </p>
      <button
        class="mt-4 px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
        @click="handleNew"
      >
        Criar primeira notificação
      </button>
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead class="text-xs uppercase bg-n-alpha-2 text-n-slate-11">
          <tr>
            <th class="px-4 py-3">Título</th>
            <th class="px-4 py-3">Inbox</th>
            <th class="px-4 py-3">Listas</th>
            <th class="px-4 py-3">Agendado para</th>
            <th class="px-4 py-3">Status</th>
            <th class="px-4 py-3">Progresso</th>
            <th class="px-4 py-3">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="notification in notifications"
            :key="notification.id"
            class="border-b border-n-strong"
          >
            <td class="px-4 py-3 font-medium text-n-slate-12">
              {{ notification.title }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ notification.inbox?.name || '-' }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              <template v-if="notification.contact_lists?.length">
                <span
                  v-for="cl in notification.contact_lists"
                  :key="cl.id"
                  class="inline-block px-1.5 py-0.5 mr-1 mb-0.5 text-xs rounded bg-n-alpha-2"
                >
                  {{ cl.name }} ({{ cl.contacts_count }})
                </span>
              </template>
              <template v-else-if="notification.contact_list">
                {{ notification.contact_list.name }}
                <span class="text-xs">
                  ({{ notification.contact_list.contacts_count }})
                </span>
              </template>
              <template v-else>-</template>
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ formatDate(notification.scheduled_at) }}
            </td>
            <td class="px-4 py-3">
              <span :class="statusColor(notification.status)" class="font-medium">
                {{ statusLabel(notification.status) }}
              </span>
            </td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ notification.sent_count || 0 }} / {{ notification.total_contacts || 0 }}
              <span v-if="notification.failed_count > 0" class="text-n-ruby-11">
                ({{ notification.failed_count }} falhas)
              </span>
            </td>
            <td class="px-4 py-3">
              <div class="flex gap-2">
                <button
                  v-if="notification.status === 'pending'"
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11"
                  @click="handleEdit(notification)"
                >
                  Editar
                </button>
                <button
                  v-if="notification.status === 'pending' || notification.status === 'running'"
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-ruby-11"
                  @click="handleCancel(notification)"
                >
                  Cancelar
                </button>
                <button
                  v-if="['completed', 'failed', 'cancelled'].includes(notification.status)"
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-blue-11"
                  @click="handleResend(notification)"
                >
                  Reenviar
                </button>
                <button
                  v-if="['completed', 'failed', 'cancelled'].includes(notification.status)"
                  class="px-2 py-1 text-xs rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-ruby-11"
                  @click="handleDelete(notification)"
                >
                  Excluir
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <NotificacaoDialog
      v-if="showDialog"
      :notification="editingNotification"
      :inboxes="inboxes"
      @close="handleCloseDialog"
    />

    <div
      v-if="confirmingResend"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black-6"
      @click.self="cancelResend"
    >
      <div class="w-full max-w-md p-6 rounded-xl bg-n-solid-2 shadow-xl">
        <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
          Confirmar reenvio
        </h3>
        <p class="text-sm text-n-slate-11 mb-1">
          Deseja reenviar a notificação
          <strong>{{ confirmingResend.title }}</strong>?
        </p>
        <p class="text-xs text-n-slate-10 mb-6">
          Os registros de envio anteriores serão apagados e a notificação será
          reenviada para todos os contatos da lista.
        </p>
        <div class="flex justify-end gap-2">
          <button
            class="px-4 py-2 text-sm rounded-lg bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11"
            @click="cancelResend"
          >
            Cancelar
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
            @click="confirmResend"
          >
            Reenviar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
